import {
  Grid,
  Box,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import GenericTextField from "../../components/GenericTextField";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import styles from "./styles.module.css";
import GenericNumberField from "../../components/GenericNumberField";
import GenericYearMonthPicker from "../../components/GenericYearMonthPicker";
import dayjs from "dayjs";
import { BRLCurrencyFormat } from "src/components/BRLCurrencyFormat";

const BasesCalculoFormSchema = z
  .object({
    idBaseCalculo: z.number().readonly(),
    referencia: z.string().min(1, {
      message: "Mês de referência é obrigatório",
    }),
    valor: z.number({
      required_error: "Valor da base de cálculo é obrigatório",
    }),
  })
  .partial({
    idBaseCalculo: true,
  })
  .required({
    referencia: true,
    valor: true,
  });

type BasesCalculoFormData = z.infer<typeof BasesCalculoFormSchema>;

type BasesCalculoFormProps = {
  basesCalculoValues: BasesCalculoFormData;
  onSubmit(data: BasesCalculoFormData): void;
  onClose(event: any): void;
};

export default function BasesCalculoForm({
  basesCalculoValues,
  onSubmit,
  onClose,
}: BasesCalculoFormProps) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<BasesCalculoFormData>({
    mode: "onChange",
    resolver: zodResolver(BasesCalculoFormSchema),
    defaultValues: {
      idBaseCalculo: basesCalculoValues.idBaseCalculo || undefined,
      referencia: basesCalculoValues.referencia || "",
      valor: +basesCalculoValues.valor || undefined,
    },
  });

  const handleCloseForm = (event: any) => {
    onClose(event);
  };

  const handleReferenciaChange = (event: Date) => {
    setValue("referencia", dayjs(event).format("YYYY-MM"));
  };

  const submit = (data: BasesCalculoFormData) => {
    const body: any = {
      baseCalculo: {
        referencia: data.referencia,
        valor: data.valor,
      },
    };

    onSubmit(body);
  };

  const handleValorChange = (event: any) => {
    const adjustedValorValue: number = event ? +event : event;
    setValue("valor", adjustedValorValue, { shouldValidate: true });
  };

  return (
    <form onSubmit={handleSubmit(submit)} className={styles.formDiv}>
      <DialogContent className={styles.formContainer}>
        <Box className={styles.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={2}>
              <Controller
                name="idBaseCalculo"
                control={control}
                defaultValue={basesCalculoValues.idBaseCalculo}
                render={({ field: { ref, ...field } }) => (
                  <GenericTextField
                    disabled
                    label="ID"
                    error={Boolean(errors.idBaseCalculo)}
                    helperText={errors.idBaseCalculo?.message}
                    placeholder={"Nova base de cálculo"}
                    inputRef={ref}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="referencia"
                control={control}
                defaultValue={basesCalculoValues.referencia}
                render={({ field: { ref, onChange, ...field } }) => (
                  <GenericYearMonthPicker
                    required
                    label="Referência"
                    inputRef={ref}
                    placeholder="Digite o mês de referência"
                    onChange={handleReferenciaChange}
                    error={Boolean(errors.referencia)}
                    helperText={errors.referencia?.message}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={7}>
              <Controller
                name="valor"
                control={control}
                defaultValue={basesCalculoValues.valor}
                render={({ field: { ref, onChange, ...field } }) => (
                  <GenericNumberField
                    required
                    label="Valor"
                    placeholder="Digite o valor"
                    error={Boolean(errors.valor)}
                    helperText={errors.valor?.message}
                    inputRef={ref}
                    decimals={2}
                    onChange={(data: any) => {
                      handleValorChange(data);
                    }}
                    InputProps={{
                      inputComponent: BRLCurrencyFormat,
                    }}
                    {...field}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box>
          <DialogActions className={styles.botoesPopupGeral}>
            <Button
              className={styles.botaoPopupCancelar}
              onClick={handleCloseForm}
              size="small"
            >
              Cancelar
            </Button>
            <Button
              className={styles.botaoPopupSalvar}
              type="submit"
              color="primary"
              size="small"
            >
              Salvar
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </form>
  );
}
